// Keeps a signed in person's product analytics choice the same on every device (docs/analytics.md): this browser's
// answer goes to the server (POST /api/me/consents, purpose analytics, with the notice version the person answered),
// and an answer made on another device comes back through /api/auth/me. The rules are lib/consent reconcile (unit
// tested). A post that fails (offline, a server error) is tried again a few times, so a "no" reaches the account
// that would otherwise keep sending. Once the account holds this browser's answer it takes the account's time for it
// (settle), so a browser clock is never compared with the server's.
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { analyticsConfigured } from '@/lib/analytics'
import { api, isApiError } from '@/lib/api'
import { ME_KEY, useMe, type Me } from '@/lib/auth'
import { readVisitChoice, reconcile } from '@/lib/consent'
import { useConsentStore } from '@/lib/consentStore'

type ConsentRow = { granted: boolean; source: string; version: string; createdAt: string }

/** Waits before each new try of a failed post; after the last one the next visit or change tries again. */
export const CONSENT_RETRY_MS = [2_000, 10_000, 60_000, 300_000]

export function useAnalyticsConsentSync(): void {
  const qc = useQueryClient()
  const meQuery = useMe()
  const user = meQuery.data?.user ?? null
  // signed out only on an answer from the API (a failed call, the backend down, says nothing about the session)
  const known = meQuery.isSuccess
  const { stored, decision, signal } = useConsentStore(
    useShallow((s) => ({ stored: s.stored, decision: s.decision, signal: s.signal })),
  )
  const busy = useRef(false)
  const failures = useRef(0)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [attempt, setAttempt] = useState(0)
  const server = user?.analyticsConsent ?? null
  const userId = user?.id

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  // who is signed in, and the account's answer this browser knows (a choice made here records it as its basis); a
  // session that ended gives the browser its own answer back
  useEffect(() => {
    if (known) useConsentStore.getState().setViewer(userId ?? null, server?.at ?? null)
  }, [known, userId, server?.at])

  useEffect(() => {
    if (!userId || !analyticsConfigured() || busy.current) return
    const store = useConsentStore.getState()
    if (store.viewer !== userId) store.setViewer(userId, server?.at ?? null)
    const local = useConsentStore.getState().stored
    const { post, adopt, settle } = reconcile(server, local, decision, signal, userId, readVisitChoice())
    if (settle && local) useConsentStore.getState().settle(local, settle)
    if (adopt && (adopt.state !== local?.state || adopt.at !== local?.at || adopt.source !== local?.source || !local?.adopted)) {
      useConsentStore.getState().adopt(adopt, userId)
    }
    if (!post) return
    busy.current = true
    // this browser's answer that goes (none for a privacy signal): it takes the account's time once it is there
    const answered = local && post.source === local.source && post.granted === (local.state === 'granted') ? local : null
    api
      .post<ConsentRow>('/api/me/consents', { purpose: 'analytics', granted: post.granted, source: post.source, version: post.version })
      .then((row) => {
        failures.current = 0
        const analyticsConsent = { granted: row.granted, source: row.source, version: row.version, at: row.createdAt }
        if (answered) useConsentStore.getState().settle(answered, row.createdAt)
        qc.setQueryData<Me | null>(ME_KEY, (old) => (old ? { ...old, user: { ...old.user, analyticsConsent } } : old))
      })
      .catch((e: unknown) => {
        // analytics must never break the app; offline (status 0), a server error or a rate limit is tried again, while
        // a refused answer (another 4xx) would be refused again
        const retry = !isApiError(e) || e.status === 0 || e.status >= 500 || e.status === 429
        const wait = CONSENT_RETRY_MS[failures.current]
        if (!retry || wait === undefined) return
        failures.current += 1
        timer.current = setTimeout(() => setAttempt((n) => n + 1), wait)
      })
      .finally(() => {
        busy.current = false
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the answer's fields, not object identity
  }, [userId, server?.granted, server?.source, server?.at, stored?.state, stored?.source, stored?.at, stored?.pending, stored?.owner, stored?.adopted, decision.reason, decision.mode, signal, attempt])
}
